import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { assetHoldings, marginPositions, futuresPositions } from '../../utils/portfolioData';
// Spot Holdings Card with Navigation
function SpotHoldingCard() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  // Sort by value (amount * price) descending
  const sortedHoldings = [...assetHoldings].filter(h => h.spotAmount > 0).sort((a, b) => b.spotAmount * b.price - a.spotAmount * a.price);
  if (sortedHoldings.length === 0) return null;
  const holding = sortedHoldings[currentIndex];
  const value = holding.spotAmount * holding.price;
  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev > 0 ? prev - 1 : sortedHoldings.length - 1);
  };
  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev < sortedHoldings.length - 1 ? prev + 1 : 0);
  };
  const handleCardClick = () => {
    navigate('/portfolio?account=main');
  };
  return <button onClick={handleCardClick} className="glass-border glass-border-hover rounded-[16px] p-3 flex flex-col gap-2 transition-colors cursor-pointer text-left w-full" data-id="element-896">
      <div className="flex items-center justify-between" data-id="element-897">
        <span className="text-[10px] text-text-secondary font-label uppercase tracking-wider" data-id="element-898">
          Spot Holding
        </span>
        {sortedHoldings.length > 1 && <div className="flex items-center gap-1" data-id="element-899">
            <button onClick={handlePrev} className="p-0.5 hover:bg-background-elevated rounded transition-colors text-text-secondary hover:text-text-primary" data-id="element-900">
              <ChevronLeft size={14} data-id="element-901" />
            </button>
            <span className="text-[10px] text-text-tertiary font-mono" data-id="element-902">
              {currentIndex + 1}/{sortedHoldings.length}
            </span>
            <button onClick={handleNext} className="p-0.5 hover:bg-background-elevated rounded transition-colors text-text-secondary hover:text-text-primary" data-id="element-903">
              <ChevronRight size={14} data-id="element-904" />
            </button>
          </div>}
      </div>

      <div className="flex items-center gap-2" data-id="element-905">
        <img src={holding.logo} alt={holding.symbol} className="w-6 h-6 object-contain" data-id="element-906" />
        <div className="flex-1 min-w-0" data-id="element-907">
          <div className="text-sm font-semibold text-text-primary" data-id="element-908">
            {holding.symbol}
          </div>
          <div className="text-[10px] text-text-secondary" data-id="element-909">{holding.name}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border-subtle" data-id="element-910">
        <div data-id="element-911">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-left" data-id="element-912">
            Amount
          </div>
          <div className="text-xs font-numbers text-text-primary text-left" data-id="element-913">
            {holding.spotAmount.toLocaleString('en-US', {
            minimumFractionDigits: holding.symbol === 'USDT' ? 2 : 4,
            maximumFractionDigits: holding.symbol === 'USDT' ? 2 : 4
          })}
          </div>
        </div>
        <div data-id="element-914">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-right" data-id="element-915">
            Value
          </div>
          <div className="text-xs font-numbers text-text-primary font-medium text-right" data-id="element-916">
            {value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}{' '}
            <span className="text-text-secondary" data-id="element-917">USDT</span>
          </div>
        </div>
      </div>
    </button>;
}
// Margin Position Card with Navigation
function MarginPositionCard() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  if (marginPositions.length === 0) return null;
  const position = marginPositions[currentIndex];
  const handlePrev = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : marginPositions.length - 1);
  };
  const handleNext = () => {
    setCurrentIndex(prev => prev < marginPositions.length - 1 ? prev + 1 : 0);
  };
  const handleManageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/trading');
  };
  return <div className="glass-border rounded-[16px] p-3 flex flex-col gap-2" data-id="element-918">
      <div className="flex items-center justify-between" data-id="element-919">
        <span className="text-[10px] text-text-secondary font-label uppercase tracking-wider" data-id="element-920">
          Margin Position
        </span>
        <button onClick={handleManageClick} className="text-[10px] text-brand-purple-lighter hover:text-white transition-colors font-medium flex items-center gap-0.5" data-id="element-921">
          Manage
          <ExternalLink size={10} data-id="element-922" />
        </button>
      </div>

      <div className="flex items-center gap-2" data-id="element-923">
        <img src={position.logo} alt={position.symbol} className="w-6 h-6 object-contain" data-id="element-924" />
        <div className="flex-1 min-w-0 flex items-center gap-2" data-id="element-925">
          <span className="text-sm font-semibold text-text-primary" data-id="element-926">
            {position.market}
          </span>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${position.side === 'Long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`} data-id="element-927">
            {position.side}
          </span>
          <span className="text-[10px] text-text-tertiary bg-background-elevated px-1.5 py-0.5 rounded" data-id="element-928">
            {position.leverage}x
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border-subtle" data-id="element-929">
        <div data-id="element-930">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-left" data-id="element-931">
            Size
          </div>
          <div className="text-xs font-numbers text-text-primary text-left" data-id="element-932">
            {position.openQuantity.toFixed(8)} {position.symbol}
          </div>
        </div>
        <div data-id="element-933">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-right" data-id="element-934">
            UP&L
          </div>
          <div className="text-right" data-id="element-935">
            <div className={`text-xs font-numbers font-medium ${position.unrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-936">
              {position.unrealizedPnl >= 0 ? '+' : ''}
              {position.unrealizedPnl.toFixed(2)}{' '}
              <span className="text-text-secondary" data-id="element-937">USDT</span>
            </div>
            <div className="text-[10px] text-text-secondary" data-id="element-938">
              ({position.unrealizedPnlPercent >= 0 ? '+' : ''}
              {position.unrealizedPnlPercent.toFixed(2)}%)
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// Futures Position Card with Navigation
function FuturesPositionCard() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  if (futuresPositions.length === 0) return null;
  const position = futuresPositions[currentIndex];
  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev > 0 ? prev - 1 : futuresPositions.length - 1);
  };
  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev < futuresPositions.length - 1 ? prev + 1 : 0);
  };
  const handleManageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/trading');
  };
  return <div className="glass-border rounded-[16px] p-3 flex flex-col gap-2" data-id="element-939">
      <div className="flex items-center justify-between" data-id="element-940">
        <span className="text-[10px] text-text-secondary font-label uppercase tracking-wider" data-id="element-941">
          Futures Position
        </span>
        <div className="flex items-center gap-2" data-id="element-942">
          {futuresPositions.length > 1 && <div className="flex items-center gap-1" data-id="element-943">
              <button onClick={handlePrev} className="p-0.5 hover:bg-background-elevated rounded transition-colors text-text-secondary hover:text-text-primary" data-id="element-944">
                <ChevronLeft size={14} data-id="element-945" />
              </button>
              <span className="text-[10px] text-text-tertiary font-mono" data-id="element-946">
                {currentIndex + 1}/{futuresPositions.length}
              </span>
              <button onClick={handleNext} className="p-0.5 hover:bg-background-elevated rounded transition-colors text-text-secondary hover:text-text-primary" data-id="element-947">
                <ChevronRight size={14} data-id="element-948" />
              </button>
            </div>}
          <button onClick={handleManageClick} className="text-[10px] text-brand-purple-lighter hover:text-white transition-colors font-medium flex items-center gap-0.5" data-id="element-949">
            Manage
            <ExternalLink size={10} data-id="element-950" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2" data-id="element-951">
        <img src={position.logo} alt={position.symbol} className="w-6 h-6 object-contain" data-id="element-952" />
        <div className="flex-1 min-w-0 flex items-center gap-2" data-id="element-953">
          <span className="text-sm font-semibold text-text-primary" data-id="element-954">
            {position.pair}
          </span>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${position.side === 'Long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`} data-id="element-955">
            {position.side}
          </span>
          <span className="text-[10px] text-text-tertiary bg-background-elevated px-1.5 py-0.5 rounded" data-id="element-956">
            {position.leverage}x
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border-subtle" data-id="element-957">
        <div data-id="element-958">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-left" data-id="element-959">
            Size
          </div>
          <div className="text-xs font-numbers text-text-primary text-left" data-id="element-960">
            {position.size} {position.symbol}
          </div>
        </div>
        <div data-id="element-961">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-right" data-id="element-962">
            UP&L
          </div>
          <div className="text-right" data-id="element-963">
            <div className={`text-xs font-numbers font-medium ${position.unrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-964">
              {position.unrealizedPnl >= 0 ? '+' : ''}
              {position.unrealizedPnl.toFixed(2)}{' '}
              <span className="text-text-secondary" data-id="element-965">USDT</span>
            </div>
            <div className="text-[10px] text-text-secondary" data-id="element-966">
              ({position.unrealizedPnlPercent >= 0 ? '+' : ''}
              {position.unrealizedPnlPercent.toFixed(2)}%)
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// Earn Position Card (placeholder - no data yet)
function EarnPositionCard() {
  // Mock earn positions - in real app this would come from portfolioData
  const earnPositions: any[] = [];
  if (earnPositions.length === 0) return null;
  return <div className="glass-border rounded-[16px] p-3 flex flex-col gap-2" data-id="element-967">
      <div className="flex items-center justify-between" data-id="element-968">
        <span className="text-[10px] text-text-secondary font-label uppercase tracking-wider" data-id="element-969">
          Earn Position
        </span>
      </div>
      {/* Earn position content */}
    </div>;
}
export function StatsCards() {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-970">
      <SpotHoldingCard data-id="element-971" />
      <MarginPositionCard data-id="element-972" />
      <FuturesPositionCard data-id="element-973" />
      <EarnPositionCard data-id="element-974" />
    </div>;
}